/**
 * Module dependencies.
 */

import { Title } from 'app/components/atoms/title/title';
import { useTranslation } from 'react-i18next';
import { useGetAppsCryptoOverview } from 'app/hooks/requests/apps-crypto/use-get-apps-crypto-overview';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BadgeContainer,
  Cell,
  Content,
  GridTable,
  HeaderCell,
  HeaderRow,
  HeaderTable,
  Icon,
  Row,
  SpaceBetween
} from './overview.styles';
import { formatNumber } from '../transactions/form-config';
import BigNumber from 'bignumber.js';
import { Expanded } from 'app/components/organisms/table/expanded';
import { Badge } from 'app/components/ui/badge';
import { Filters, HeaderSearch } from './components/header-search';
import { useSearchParams } from 'react-router-dom';
import { compact } from 'lodash';
import { TransactionsList } from './components/transactions-list';
import { NeedUpdate } from './need-update';
import { NeedUpdateModal } from './need-update-modal';
import { FixAmountsModal } from './fix-amounts-modal';

/**
 * `CellShow` type.
 */

type CellShow = {
  item: string;
  visible: boolean;
};

/**
 * Export `AppsCryptoOverview` component.
 */

export function AppsCryptoOverview(): JSX.Element {
  const [translate] = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pause, setPause] = useState(false);
  const [fixAmounts, setFixAmounts] = useState(false);
  const [selectedItem, setItem] = useState<any>(null);
  const [expandedCell, expandCell] = useState<CellShow>({
    item: '',
    visible: false
  });

  const [filters, setFilters] = useState<Filters>({
    providers: compact(searchParams.get('providers')?.split(',')).map(item => parseFloat(item)),
    coins: compact(searchParams.get('coins')?.split(',')),
    search: null
  });

  useEffect(() => {
    if (filters) {
      setSearchParams(params => {
        params.set('coins', filters.coins.join(','));
        params.set('providers', filters.providers.join(','));

        return params;
      });
    }
  }, [filters, setSearchParams]);

  const { data, refetch } = useGetAppsCryptoOverview(filters);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    async function loop() {
      if (filters) {
        refetch();
      }

      if (timer) {
        clearTimeout(timer);
      }

      if (pause) {
        return;
      }

      timer = setTimeout(() => {
        loop();
      }, 3000);
    }

    loop();

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [filters, pause, refetch]);

  const untrackedAmounts = useMemo(() => {
    let result = new BigNumber(0);

    for (const item of data?.coins ?? []) {
      if (item.sync?.diffUsd) {
        result = result.plus(item.sync.diffUsd);
      }
    }
    return result;
  }, [data?.coins]);

  const openSync = useCallback((item: any) => {
    setItem(item);
  }, []);

  return (
    <>
      <div>
        <div>
          <Title>{translate('crypto.assets.title')}</Title>

          <HeaderSearch
            coinsList={data?.lists?.coins ?? []}
            filters={filters}
            fixAmounts={() => setFixAmounts(true)}
            providersList={data?.lists?.providers ?? []}
            refetch={() => refetch()}
            setFilters={setFilters}
          />

          <HeaderTable columns={10}>
            <HeaderCell>{'Investment'}</HeaderCell>
            <HeaderCell>{'Total'}</HeaderCell>
            <HeaderCell>{'Profit'}</HeaderCell>
            <HeaderCell>{'Percentage'}</HeaderCell>
            <HeaderCell>{'Positive'}</HeaderCell>
            <HeaderCell>{'Negative'}</HeaderCell>
            <HeaderCell>{'Positive Count'}</HeaderCell>
            <HeaderCell>{'Negative Count'}</HeaderCell>
            <HeaderCell>{'unTracked Amounts'}</HeaderCell>
            <HeaderCell>{'Airdrop ITEMS'}</HeaderCell>

            <Row>
              <Cell center>{formatNumber(data?.invest, '$')}</Cell>
              <Cell center>{formatNumber(data?.total, '$')}</Cell>
              <Cell center>{formatNumber(data?.profit, '$')}</Cell>
              <Cell center>
                {new BigNumber(data?.total ?? 0)
                  .dividedBy(data?.invest ?? 0)
                  .multipliedBy(100)
                  .minus(100)
                  .toFixed(2) + '%'}
              </Cell>
              <Cell center>{formatNumber(data?.positive, '$')}</Cell>
              <Cell center>{formatNumber(data?.negative, '$')}</Cell>
              <Cell center>{data?.positiveCount}</Cell>
              <Cell center>{data?.negativeCount}</Cell>
              <Cell center>{formatNumber(untrackedAmounts.toString(), '$')}</Cell>
              <Cell center>{formatNumber(data?.totalAirdrop, '$')}</Cell>
            </Row>
          </HeaderTable>

          <GridTable columns={9}>
            <HeaderRow>
              <HeaderCell sticky={65} width={100}>
                {'Coin'}
              </HeaderCell>

              <HeaderCell sticky={65}>{'Exchange'}</HeaderCell>
              <HeaderCell sticky={65}>{'Amount'}</HeaderCell>
              <HeaderCell sticky={65}>{'Total'}</HeaderCell>
              <HeaderCell sticky={65}>{'Unit Price'}</HeaderCell>
              <HeaderCell sticky={65}>{'Actual'}</HeaderCell>
              <HeaderCell sticky={65}>{'Total'}</HeaderCell>
              <HeaderCell sticky={65}>{'Profit'}</HeaderCell>
              <HeaderCell sticky={65}>{'Profit Dollar'}</HeaderCell>
            </HeaderRow>

            {(data?.coins ?? []).map((item, index) => {
              const isNegative = item.type === 'running' && new BigNumber(item.profitDollar).isLessThan(0);
              const isPositive = item.type === 'running' && new BigNumber(item.profitDollar).isGreaterThan(0);
              const code = [item.coin, item.provider.name].join('-');
              const tradeTransactions = item.transactions.filter(item => item.type === 'trade');
              const hasProfit = new BigNumber(item.transactionsStatus.tradeProfitDollar).isGreaterThan(0);

              return (
                <>
                  <Row
                    isSelected={expandedCell.item === code}
                    key={index}
                    onClick={() => {
                      expandCell({
                        item: code,
                        visible: expandedCell.visible ? expandedCell.item !== code : true
                      });
                    }}
                  >
                    <Cell isNegative={isNegative} isPositive={isPositive} type={item.type} width={220}>
                      <SpaceBetween>
                        <div>
                          <Icon src={item.icon} />

                          {item.coin}

                          {hasProfit ? (
                            <Badge className={'ml-4 border-transparent bg-[#87d068] text-white'}>{'PROFIT'}</Badge>
                          ) : (
                            ''
                          )}
                        </div>

                        <BadgeContainer>
                          {item.positiveTransactions > 0 && (
                            <Badge className={'border-transparent text-white'} style={{ backgroundColor: '#52c41a' }}>
                              {item.positiveTransactions}
                            </Badge>
                          )}

                          {tradeTransactions.length > 0 && (
                            <Badge
                              className={'border-transparent text-white'}
                              style={{
                                backgroundColor:
                                  tradeTransactions.length === item.positiveTransactions ? '#52c41a' : '#999'
                              }}
                              title={'done'}
                            >
                              {tradeTransactions.length}
                            </Badge>
                          )}

                          {item.transactions.length > 0 && (
                            <Badge
                              className={'border-transparent text-black'}
                              style={{ backgroundColor: '#e0e0e0' }}
                              title={'done'}
                            >
                              {item.transactions.length}
                            </Badge>
                          )}
                        </BadgeContainer>
                      </SpaceBetween>
                    </Cell>

                    <Cell isNegative={isNegative} isPositive={isPositive} type={item.type}>
                      {item.provider?.name}
                    </Cell>

                    <Cell isNegative={isNegative} isPositive={isPositive} right type={item.type}>
                      <NeedUpdate item={item} openSync={openSync} />
                      {item.amount}
                    </Cell>

                    <Cell isNegative={isNegative} isPositive={isPositive} right type={item.type}>
                      {formatNumber(item.totalInvest, '$')}
                    </Cell>

                    <Cell isNegative={isNegative} isPositive={isPositive} right type={item.type}>
                      {formatNumber(item.unitPrice, '$')}
                    </Cell>

                    <Cell isNegative={isNegative} isPositive={isPositive} right type={item.type}>
                      {formatNumber(item.actualPrice, '$')}
                    </Cell>

                    <Cell isNegative={isNegative} isPositive={isPositive} right type={item.type}>
                      {formatNumber(item.actualTotal, '$')}
                    </Cell>

                    <Cell isNegative={isNegative} isPositive={isPositive} right type={item.type}>
                      {item.profitPercentage === 'N/A'
                        ? 'N/A'
                        : item.profitPercentage === 'AIRDROP'
                        ? 'AIRDROP'
                        : new BigNumber(item.profitPercentage).toFixed(2) + '%'}
                    </Cell>

                    <Cell isNegative={isNegative} isPositive={isPositive} right type={item.type}>
                      {formatNumber(item.profitDollar, '$')}
                    </Cell>
                  </Row>

                  <Row disableHover>
                    <Cell collapse={9}>
                      <Expanded expanded={code == expandedCell.item && expandedCell.visible}>
                        <Content>
                          <TransactionsList
                            list={
                              data?.coins.find(item => [item.coin, item.provider.name].join('-') === expandedCell.item)
                                ?.transactions
                            }
                            refetch={() => refetch()}
                            setPause={setPause}
                            status={
                              data?.coins.find(item => [item.coin, item.provider.name].join('-') === expandedCell.item)
                                ?.transactionsStatus
                            }
                          />
                        </Content>
                      </Expanded>
                    </Cell>
                  </Row>
                </>
              );
            })}
          </GridTable>
        </div>
      </div>

      <FixAmountsModal
        data={data}
        isOpen={fixAmounts}
        onClose={() => setFixAmounts(false)}
        refetch={async () => {
          selectedItem.sync.needSync = false;
        }}
      />

      <NeedUpdateModal
        item={selectedItem}
        refetch={async () => {
          selectedItem.sync.needSync = false;
          //await refetch();
        }}
        setItem={setItem}
      />
    </>
  );
}
